import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/common/Header/Header';
import Footer from '../../components/common/Footer/Footer';
import BlogSidebar from '../../components/common/BlogSidebar';
import SiridhariniBlogService from '../../services/siridhariniBlogService';

const BlogDetailPage = () => {
  const { id } = useParams(); // This could be slug or ID
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [similarBlogs, setSimilarBlogs] = useState([]);

  // Scroll to top when component mounts or when id changes
  useEffect(() => {
    window.scrollTo(0, 0);
    loadBlogData();
  }, [id]);

  const loadBlogData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Try to fetch by slug first (since URLs will use slugs), fallback to ID
      let blogData;
      try {
        blogData = await SiridhariniBlogService.getBlogBySlug(id);
      } catch (slugError) {
        // If slug fails and id looks like a number, try fetching by ID
        if (!isNaN(id)) {
          console.log('Slug fetch failed, trying ID fetch...');
          // For now, we don't have a getBlogById method, so we'll get all blogs and filter
          const allBlogs = await SiridhariniBlogService.getAllPublishedBlogs();
          blogData = allBlogs.find(blog => blog.id === parseInt(id));
          if (!blogData) {
            throw new Error('Blog not found');
          }
        } else {
          throw slugError;
        }
      }

      setBlog(blogData);

      // Load similar blogs
      if (blogData.id) {
        try {
          const similar = await SiridhariniBlogService.getSimilarBlogs(blogData.id, 3);
          setSimilarBlogs(similar);
        } catch (similarError) {
          console.log('Failed to load similar blogs:', similarError);
          setSimilarBlogs([]);
        }
      }

    } catch (error) {
      console.error('Error loading blog:', error);
      setError(error.message || 'Blog not found');
    } finally {
      setLoading(false);
    }
  };

  // Function to get hero background image
  const getHeroBackgroundImage = (blog) => {
    if (blog?.featuredImageUrl) return blog.featuredImageUrl;
    if (blog?.images && blog.images.length > 0) return blog.images[0];
    if (blog?.image) return blog.image;
    return null; // No background image
  };
  
  // Define styles early to avoid initialization issues
  const styles = {
    container: {
      fontFamily: 'Arial, sans-serif',
      lineHeight: '1.6',
      color: '#333'
    },
    heroSection: {
      ...(getHeroBackgroundImage(blog) && {
        backgroundImage: `url("${getHeroBackgroundImage(blog)}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }),
      backgroundColor: '#4A5D23',
      position: 'relative',
      color: 'white',
      padding: '200px 0 100px 0',
      minHeight: '70vh',
      textAlign: 'left'
    },
    heroOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.6)'
    },
    heroContent: {
      position: 'relative',
      zIndex: 2,
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 2rem'
    },
    heroTitle: {
      fontSize: '3rem',
      fontWeight: '700',
      textTransform: 'uppercase',
      marginBottom: '1rem'
    },
    heroMeta: {
      fontSize: '1.1rem',
      opacity: '0.9'
    },
    contentSection: {
      padding: '80px 0',
      backgroundColor: '#ffffff'
    },
    sectionContainer: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 2rem'
    },
    blogLayout: {
      display: 'grid',
      gridTemplateColumns: '2fr 1fr',
      gap: '4rem',
      marginTop: '3rem'
    },
    mainContent: {
      display: 'flex',
      flexDirection: 'column'
    },
    backBtn: {
      padding: '0.8rem 2rem',
      border: '2px solid #333',
      backgroundColor: 'transparent',
      color: '#333',
      borderRadius: '25px',
      fontSize: '0.9rem',
      fontWeight: '600',
      textTransform: 'uppercase',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginBottom: '3rem'
    },
    content: {
      fontSize: '1.1rem',
      lineHeight: '1.8',
      color: '#444'
    },
    imagesGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '2rem',
      margin: '3rem 0'
    },
    image: {
      width: '100%',
      height: '250px',
      objectFit: 'cover',
      borderRadius: '8px',
      boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
    },
    notFound: {
      textAlign: 'center',
      padding: '100px 2rem',
      minHeight: '50vh'
    }
  };
  
  // Loading state
  if (loading) {
    return (
      <div style={styles.container}>
        <Header />
        <div style={{
          ...styles.notFound,
          fontSize: '1.2rem',
          color: '#666'
        }}>
          <h1>Loading...</h1>
          <p>Please wait while we load the blog content.</p>
        </div>
        <Footer />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={styles.container}>
        <Header />
        <div style={styles.notFound}>
          <h1>Blog Not Found</h1>
          <p style={{ color: '#dc3545', marginBottom: '2rem' }}>{error}</p>
          <button onClick={() => navigate('/blogs')} style={styles.backBtn}>
            Back to Blogs
          </button>
          <button 
            onClick={loadBlogData} 
            style={{
              ...styles.backBtn,
              marginLeft: '1rem',
              background: '#dc3545',
              borderColor: '#dc3545'
            }}
          >
            Retry
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  // No blog found state
  if (!blog) {
    return (
      <div style={{fontFamily: 'Arial, sans-serif', lineHeight: '1.6', color: '#333'}}>
        <Header />
        <div style={{textAlign: 'center', padding: '100px 2rem', minHeight: '50vh'}}>
          <h1>Blog Not Found</h1>
          <p>The blog you're looking for doesn't exist or has been moved.</p>
          <button 
            onClick={() => navigate('/blogs')} 
            style={{
              padding: '0.8rem 2rem',
              border: '2px solid #333',
              backgroundColor: 'transparent',
              color: '#333',
              borderRadius: '25px',
              fontSize: '0.9rem',
              fontWeight: '600',
              textTransform: 'uppercase',
              cursor: 'pointer'
            }}
          >
            Back to Blogs
          </button>
        </div>
        <Footer />
      </div>
    );
  }


  return (
    <>
      <style>{`
        .back-btn:hover {
          background-color: #333;
          color: white;
        }
        
        .content h2 {
          color: #2d5016;
          font-size: 2rem;
          margin: 2rem 0 1rem 0;
          text-transform: uppercase;
        }
        
        .content h3 {
          color: #2d5016;
          font-size: 1.5rem;
          margin: 1.5rem 0 1rem 0;
          text-transform: uppercase;
        }
        
        .content p {
          margin-bottom: 1.5rem;
        }
        
        .content ul {
          margin: 1rem 0 1.5rem 2rem;
        }
        
        .content li {
          margin-bottom: 0.5rem;
        }
        
        @media (max-width: 768px) {
          .hero-title {
            font-size: 2rem !important;
          }
          .images-grid {
            grid-template-columns: 1fr !important;
          }
          .blog-layout {
            grid-template-columns: 1fr !important;
            gap: 2rem !important;
          }
        }
      `}</style>
      
      <div style={styles.container}>
        <Header />
        
        <section style={styles.heroSection}>
          <div style={styles.heroOverlay}></div>
          <div style={styles.heroContent}>
            <h1 style={styles.heroTitle} className="hero-title">{blog.title}</h1>
            <div style={styles.heroMeta}>
              {blog.date} • {blog.comments}
            </div>
          </div>
        </section>

        <section style={styles.contentSection}>
          <div style={styles.sectionContainer}>
            <div style={styles.blogLayout} className="blog-layout">
              {/* Main Content */}
              <div style={styles.mainContent}>
                <button 
                  onClick={() => navigate('/blogs')} 
                  style={styles.backBtn}
                  className="back-btn"
                >
                  ← Back to Blogs
                </button>
                
                <div 
                  style={styles.content} 
                  className="content"
                  dangerouslySetInnerHTML={{ __html: blog.content }}
                />
                
                {blog.images && blog.images.length > 0 && (
                  <div style={styles.imagesGrid} className="images-grid">
                    {blog.images.map((imageUrl, index) => (
                      <img 
                        key={index}
                        src={imageUrl}
                        alt={`${blog.title} - Image ${index + 1}`}
                        style={styles.image}
                        onError={(e) => {
                          // Hide broken images
                          e.target.style.display = 'none';
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <BlogSidebar 
                onSearch={(searchTerm) => {
                  // Navigate to blogs page with search
                  navigate(`/blogs?search=${encodeURIComponent(searchTerm)}`);
                }}
                onCategoryFilter={(category) => {
                  // Navigate to blogs page with category filter
                  navigate(`/blogs?category=${encodeURIComponent(category)}`);
                }}
              />
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default BlogDetailPage;